import {
  ErrorBanner,
  errorMessage,
  Money,
  RecordList,
  Spinner,
  useReport,
  type RecordSlotProps,
} from '@databink/web/extension';

/**
 * Databink's screens around clients and their subscriptions, built only from the web SDK.
 * They add to core screens through slots the core publishes.
 */

/** The saved report the revenue card reads (config/report). */
export const MRR_REPORT = 'monthly-recurring-revenue';

/**
 * `app.dashboard`: what active subscriptions bill each month. The sum is the report's, made
 * by the database, so no amount is ever added up in the browser.
 */
export function MonthlyRevenue() {
  const report = useReport(MRR_REPORT);
  if (report.isPending) return <Spinner />;
  if (report.error) return <ErrorBanner>{errorMessage(report.error)}</ErrorBanner>;
  const rows = report.data.rows;
  if (rows.length === 0)
    return <p className="text-sm text-slate-500">No subscription is billing yet.</p>;
  return (
    <div className="space-y-2">
      {rows.map((row) => {
        const currency = String(row['c0'] ?? row['$currency'] ?? 'USD');
        const count = Number(row['c1'] ?? 0);
        return (
          <div key={currency} className="flex items-baseline justify-between gap-3">
            <span className="text-3xl font-semibold text-slate-900">
              <Money value={row['c2']} currency={currency} />
            </span>
            <span className="text-sm text-slate-500">
              a month from {count} {count === 1 ? 'subscription' : 'subscriptions'}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** `crm.party.detail.tabs`: the client's subscriptions, newest first. */
export function PartySubscriptions({ record }: RecordSlotProps) {
  return (
    <div className="space-y-3">
      <p className="max-w-2xl text-sm text-slate-600">
        A subscription is created when the client's opportunity is marked won. Fill in its fees and
        billing email, then Start billing: Stripe sends the invoices from then on.
      </p>
      <RecordList
        entity="ext.subscription"
        query={{
          filter: [{ field: 'party_id', op: 'eq', value: record.id }],
          sort: { field: 'created_at', dir: 'desc' },
        }}
        columns={[
          'plan',
          'monthly_total',
          'starts_on',
          'minimum_term_ends_on',
          'stripe_status',
          'state',
        ]}
        empty="No subscriptions"
      />
    </div>
  );
}
